import { useEffect, useState } from 'react';
import { createFilterOptions } from '@mui/material';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { Autocomplete, Chip, List } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderField } from '@atom/types/work';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';

const { ListItemText } = List;

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  customFieldView: CustomFieldView;
}

const WorkOrderEnumMultipleField = ({
  field,
  onChange,
  isDisabled,
  customFieldView,
}: Props) => {
  const [values, setValues] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');

  const styles = {
    chip: {
      marginTop: '5px',
      marginRight: '5px',
      backgroundColor: colors.neutral.ash,
      borderRadius: '20px',
      opacity: isDisabled ? 0.5 : 1,
    },
  };

  const handleChange = (newValues: string[]) => {
    setValues(newValues);
    onChange(field.id, newValues);
  };

  const handleDeselect = (value: string) => {
    if (isDisabled) {
      return;
    }

    const newValues = values.filter(option => option !== value);
    setValues(newValues);
    onChange(field.id, newValues);
  };

  useEffect(() => {
    setValues(field?.value || []);
    setQuery('');
  }, [field?.value]);

  const selectStyle = {
    width: getTaskFieldWidth(customFieldView),
  };

  return (
    <div>
      <TextOverflowTooltip
        width={getTaskFieldWidth(customFieldView)}
        text={field?.title}
        color={colors.neutral.dim}
        fontSize={fonts.sm}
      />
      <Autocomplete
        id={field?.id}
        style={selectStyle}
        multiple
        disableClearable
        options={field?.enumeration}
        disabled={isDisabled}
        inputValue={query}
        filterOptions={filterOptions}
        onInputChange={(event, newQuery) => setQuery(newQuery || '')}
        getOptionLabel={item => item}
        placeholder={!values.length ? 'Search' : ''}
        value={values}
        disableCloseOnSelect
        onChange={(event, newValues) => {
          handleChange(newValues);
        }}
        renderOption={(optionProps, item) => (
          <li key={item} {...optionProps}>
            <ListItemText primary={item} />
          </li>
        )}
        renderValue={selected => {
          return selected.map(item => (
            <Chip
              key={item}
              style={styles.chip}
              label={item}
              onDelete={() => handleDeselect(item)}
            />
          ));
        }}
      />
    </div>
  );
};

export default WorkOrderEnumMultipleField;
